import type { BillingSettingsManager } from '@/state/billing/useBillingSettings';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import { formatSeriesDay, formatUnits } from '@/state/billing/billing-format';
import { seriesColor } from '../../Experiments/Details/ExperimentStatistics/control-options';
import { AXIS_TICK, TOOLTIP_CONTENT_STYLE } from '../../Experiments/Details/ExperimentStatistics/StatisticsChart';
import {
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
} from '../../Experiments/Details/ExperimentStatistics/StatisticsChart';
import { EmptyState, Panel } from '../../ConsolePanel';
import { SectionStatusLine } from './SectionStatusLine';

type UsageChartProps = { manager: BillingSettingsManager };

const COLOR = seriesColor(0);

/**
 * Daily website sessions this month, not cumulative, and no allowance line: the
 * allowance is measured in the plan's own window, which rarely matches the
 * calendar month this chart covers. Axes, tooltip and colour come from the
 * statistics charts so the two match.
 */
export const UsageChart = ({ manager }: UsageChartProps) => {
  const { chart } = manager;
  const { series, title, hasUsage } = chart;

  return (
    <Panel tourAnchor="billing-daily" title={title} description="Website sessions per day.">
      {!series || chart.isLoading ? (
        <SectionStatusLine
          status={chart}
          loadingText="Loading daily usage..."
          errorText="Daily usage could not be loaded."
        />
      ) : !hasUsage ? (
        <EmptyState>No usage recorded yet this month.</EmptyState>
      ) : (
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <defs>
                <linearGradient id="billing-usage-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLOR} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={AXIS_TICK}
                tickMargin={8}
                minTickGap={24}
                stroke="var(--line)"
                tickLine={false}
                tickFormatter={(value) => formatSeriesDay(String(value))}
              />
              <YAxis
                width={64}
                tick={AXIS_TICK}
                stroke="var(--line)"
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                tickFormatter={(value) => formatUnits(Number(value))}
              />
              <Tooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                itemStyle={TOOLTIP_ITEM_STYLE}
                labelFormatter={(value) => formatSeriesDay(String(value))}
                formatter={(value) => [formatUnits(Number(value)), 'Sessions']}
              />
              <Area
                type="monotone"
                dataKey="units"
                stroke={COLOR}
                strokeWidth={2}
                fill="url(#billing-usage-fill)"
                activeDot={{ r: 4, stroke: COLOR, strokeWidth: 2, fill: 'hsl(var(--panel))' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
};
